import type {
  OrderDraftRequest,
  Product,
  ProductOptions,
  ScriptQuoteOptions,
} from '@noconflict/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../shared/api/supabase';
import {
  createOrderPayment,
  getOrder,
  getOrderReferenceRequired,
  getProjectOrder,
  getQuote,
  getRunStartCheck,
  getScriptQuote,
  listOrgOrders,
  listWorkspaceOrders,
  reopenOrder,
  saveOrderDraft,
  submitOrder,
  updateOrderDraft,
} from './api';

/** Everything under `['order']`; quotes under `['quote']` (staff pricing mutations refresh that prefix). */
export const orderKeys = {
  all: ['order'] as const,
  one: (orderId: string) => ['order', 'one', orderId] as const,
  project: (projectId: string) => ['order', 'project', projectId] as const,
  workspace: (workspaceId: string) => ['order', 'workspace', workspaceId] as const,
  organization: (organizationId: string) => ['order', 'organization', organizationId] as const,
  payment: (orderId: string) => ['order', 'payment', orderId] as const,
  quote: (organizationId: string, product: string, options: unknown) =>
    ['quote', organizationId, product, options] as const,
  referenceRequired: (organizationId: string) =>
    ['order', 'reference-required', organizationId] as const,
  runStart: (projectId: string) => ['order', 'run-start', projectId] as const,
};

async function sessionToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('forbidden');
  return token;
}

/** Whether the org requires a reference; cheap and stable, so the project page may always ask. */
export function useOrderReferenceRequired(organizationId: string) {
  return useQuery({
    queryKey: orderKeys.referenceRequired(organizationId),
    queryFn: () => getOrderReferenceRequired(supabase, organizationId),
    staleTime: 300_000,
  });
}

/** The run gate as the database sees it, for pages that cannot read the order itself (the staff project frame). */
export function useRunStartCheck(projectId: string, enabled = true) {
  return useQuery({
    queryKey: orderKeys.runStart(projectId),
    queryFn: () => getRunStartCheck(supabase, projectId),
    enabled,
  });
}

export function useOrder(orderId: string, options: { pollMs?: number | false } = {}) {
  return useQuery({
    queryKey: orderKeys.one(orderId),
    queryFn: () => getOrder(supabase, orderId),
    refetchInterval: options.pollMs ?? false,
  });
}

export function useProjectOrder(projectId: string, options: { pollMs?: number | false } = {}) {
  return useQuery({
    queryKey: orderKeys.project(projectId),
    queryFn: () => getProjectOrder(supabase, projectId),
    refetchInterval: options.pollMs ?? false,
  });
}

/** The organization's orders for the Orders page. Under `['order']`, so paying or placing one refreshes it. */
export function useOrgOrders(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: orderKeys.organization(organizationId),
    queryFn: () => listOrgOrders(supabase, organizationId),
    enabled,
  });
}

export function useWorkspaceOrders(workspaceId: string) {
  return useQuery({
    queryKey: orderKeys.workspace(workspaceId),
    queryFn: () => listWorkspaceOrders(supabase, workspaceId),
  });
}

export function useQuote<P extends Product>(
  organizationId: string,
  product: P,
  options: ProductOptions[P],
  enabled = true,
) {
  return useQuery({
    queryKey: orderKeys.quote(organizationId, product, options),
    queryFn: async () => getQuote(await sessionToken(), organizationId, product, options),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * The live price of a script order. The key carries the analysis id, so replacing the script (a new analysis, maybe
 * another length) asks again.
 */
export function useScriptQuote(
  organizationId: string,
  projectId: string,
  analysisId: string | null,
  options: ScriptQuoteOptions,
  enabled = true,
) {
  return useQuery({
    queryKey: orderKeys.quote(organizationId, 'script', { projectId, analysisId, ...options }),
    queryFn: async () => getScriptQuote(await sessionToken(), organizationId, projectId, options),
    enabled: enabled && analysisId !== null,
    staleTime: 60_000,
  });
}

/**
 * The client secret of the order's payment form. Never cached beyond the page: a session expires, and the worker
 * decides whether to reuse or replace it. `retry: false`: a refusal (not awaiting payment, no permission) is final.
 */
export function useOrderPayment(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.payment(orderId),
    queryFn: async () => createOrderPayment(await sessionToken(), orderId),
    enabled,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
}

/**
 * An order's state decides whether runs may start, so the `runs` prefix is refreshed with `order`; and placing an
 * order (at once, on account) makes its project ordered, so the project lists are refreshed too.
 */
function useOrderMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: orderKeys.all }),
        qc.invalidateQueries({ queryKey: ['runs'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        // an order placed on account changes the balance
        qc.invalidateQueries({ queryKey: ['org-account'] }),
      ]),
  });
}

export const useSaveOrderDraft = (projectId: string) =>
  useOrderMutation(async (draft: OrderDraftRequest) =>
    saveOrderDraft(await sessionToken(), projectId, draft),
  );
export const useUpdateOrderDraft = () =>
  useOrderMutation(async (input: { orderId: string; draft: OrderDraftRequest }) =>
    updateOrderDraft(await sessionToken(), input.orderId, input.draft),
  );
export const useSubmitOrder = () =>
  useOrderMutation(async (orderId: string) => submitOrder(await sessionToken(), orderId));

/** Reopening makes the project editable again, so the project lists are refreshed with the order. */
export function useReopenOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => reopenOrder(await sessionToken(), orderId),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: orderKeys.all }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
      ]),
  });
}
